import { ExtendedWebsiteMessage, MessageFilters } from '../types/messages';

interface MessageCache {
  messages: Map<string, ExtendedWebsiteMessage>;
  searchResults: Map<string, string[]>; // query -> messageIds
  filterResults: Map<string, string[]>; // filterKey -> messageIds
  lastUpdated: number;
  ttl: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Message caching service for performance optimization
 * Requirements: 1.2, 5.1, 6.1 - Performance optimizations
 */
export class MessageCacheService {
  private static cache: MessageCache = {
    messages: new Map(),
    searchResults: new Map(),
    filterResults: new Map(),
    lastUpdated: 0,
    ttl: 5 * 60 * 1000 // 5 minutes
  };

  private static userMessageCache = new Map<string, CacheEntry<ExtendedWebsiteMessage[]>>();
  private static readonly USER_CACHE_TTL = 2 * 60 * 1000; // 2 minutes for user messages
  private static readonly SEARCH_CACHE_TTL = 10 * 60 * 1000; // 10 minutes for search results
  private static readonly FILTER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes for filter results

  /**
   * Cache user messages
   */
  static cacheUserMessages(userId: string, messages: ExtendedWebsiteMessage[]): void {
    try {
      // Update individual message cache
      messages.forEach(message => {
        this.cache.messages.set(message.id, message);
      });

      // Update user-specific cache
      this.userMessageCache.set(userId, {
        data: messages,
        timestamp: Date.now(),
        ttl: this.USER_CACHE_TTL
      });

      this.cache.lastUpdated = Date.now();
    } catch (error) {
      console.error('Error caching user messages:', error);
    }
  }

  /**
   * Get cached user messages
   */
  static getCachedUserMessages(userId: string): ExtendedWebsiteMessage[] | null {
    try {
      const cached = this.userMessageCache.get(userId);
      
      if (!cached) return null;
      
      const now = Date.now();
      if (now - cached.timestamp > cached.ttl) {
        this.userMessageCache.delete(userId);
        return null;
      }

      return cached.data;
    } catch (error) {
      console.error('Error getting cached user messages:', error);
      return null;
    }
  }

  /**
   * Cache search results
   */
  static cacheSearchResults(query: string, messageIds: string[]): void {
    try {
      const cacheKey = this.generateSearchCacheKey(query);
      this.cache.searchResults.set(cacheKey, messageIds);
      
      // Clean old search cache entries
      this.cleanSearchCache();
    } catch (error) {
      console.error('Error caching search results:', error);
    }
  }

  /**
   * Get cached search results
   */
  static getCachedSearchResults(query: string): string[] | null {
    try {
      const cacheKey = this.generateSearchCacheKey(query);
      return this.cache.searchResults.get(cacheKey) || null;
    } catch (error) {
      console.error('Error getting cached search results:', error);
      return null;
    }
  }

  /**
   * Cache filter results
   */
  static cacheFilterResults(filters: MessageFilters, messageIds: string[]): void {
    try {
      const cacheKey = this.generateFilterCacheKey(filters);
      this.cache.filterResults.set(cacheKey, messageIds);
      
      // Clean old filter cache entries
      this.cleanFilterCache();
    } catch (error) {
      console.error('Error caching filter results:', error);
    }
  }

  /**
   * Get cached filter results
   */
  static getCachedFilterResults(filters: MessageFilters): string[] | null {
    try {
      const cacheKey = this.generateFilterCacheKey(filters);
      return this.cache.filterResults.get(cacheKey) || null;
    } catch (error) {
      console.error('Error getting cached filter results:', error);
      return null;
    }
  }

  /**
   * Get cached message by ID
   */
  static getCachedMessage(messageId: string): ExtendedWebsiteMessage | null {
    try {
      return this.cache.messages.get(messageId) || null;
    } catch (error) {
      console.error('Error getting cached message:', error);
      return null;
    }
  }

  /**
   * Update cached message
   */
  static updateCachedMessage(message: ExtendedWebsiteMessage): void {
    try {
      this.cache.messages.set(message.id, message);
      
      // Update in user caches as well
      this.userMessageCache.forEach((cached, userId) => {
        const messageIndex = cached.data.findIndex(m => m.id === message.id);
        if (messageIndex >= 0) {
          cached.data[messageIndex] = message;
        }
      });
    } catch (error) {
      console.error('Error updating cached message:', error);
    }
  }

  /**
   * Remove message from cache
   */
  static removeCachedMessage(messageId: string): void {
    try {
      this.cache.messages.delete(messageId);
      
      // Remove from user caches as well
      this.userMessageCache.forEach((cached, userId) => {
        cached.data = cached.data.filter(m => m.id !== messageId);
      });
      
      // Clear related search and filter caches
      this.cache.searchResults.clear();
      this.cache.filterResults.clear();
    } catch (error) {
      console.error('Error removing cached message:', error);
    }
  }

  /**
   * Invalidate user cache
   */
  static invalidateUserCache(userId: string): void {
    try {
      this.userMessageCache.delete(userId);
      
      // Also clear search and filter caches as they might be stale
      this.cache.searchResults.clear();
      this.cache.filterResults.clear();
    } catch (error) {
      console.error('Error invalidating user cache:', error);
    }
  }

  /**
   * Clear all caches
   */
  static clearAllCaches(): void {
    try {
      this.cache.messages.clear();
      this.cache.searchResults.clear();
      this.cache.filterResults.clear();
      this.userMessageCache.clear();
      this.cache.lastUpdated = 0;
    } catch (error) {
      console.error('Error clearing all caches:', error);
    }
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): {
    messageCount: number;
    userCacheCount: number;
    searchCacheCount: number;
    filterCacheCount: number;
    lastUpdated: number;
    memoryUsage: number;
  } {
    try {
      // Estimate memory usage (rough calculation)
      const messageMemory = this.cache.messages.size * 1024; // ~1KB per message
      const userCacheMemory = this.userMessageCache.size * 10240; // ~10KB per user cache
      const searchMemory = this.cache.searchResults.size * 512; // ~512B per search result
      const filterMemory = this.cache.filterResults.size * 512; // ~512B per filter result
      
      return {
        messageCount: this.cache.messages.size,
        userCacheCount: this.userMessageCache.size,
        searchCacheCount: this.cache.searchResults.size,
        filterCacheCount: this.cache.filterResults.size,
        lastUpdated: this.cache.lastUpdated,
        memoryUsage: messageMemory + userCacheMemory + searchMemory + filterMemory
      };
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return {
        messageCount: 0,
        userCacheCount: 0,
        searchCacheCount: 0,
        filterCacheCount: 0,
        lastUpdated: 0,
        memoryUsage: 0
      };
    }
  }

  /**
   * Generate search cache key
   */
  private static generateSearchCacheKey(query: string): string {
    return `search:${query.toLowerCase().trim()}`;
  }

  /**
   * Generate filter cache key
   */
  private static generateFilterCacheKey(filters: MessageFilters): string {
    const filterParts = [
      `readStatus:${filters.readStatus}`,
      `type:${filters.type}`,
      `source:${filters.source}`,
      `bookmarked:${filters.bookmarked || false}`,
      `dateStart:${filters.dateRange.start || ''}`,
      `dateEnd:${filters.dateRange.end || ''}`
    ];
    
    return `filter:${filterParts.join('|')}`;
  }

  /**
   * Clean old search cache entries
   */
  private static cleanSearchCache(): void {
    try {
      if (this.cache.searchResults.size > 100) {
        // Keep only the 50 most recent entries
        const entries = Array.from(this.cache.searchResults.entries());
        const toKeep = entries.slice(-50);
        
        this.cache.searchResults.clear();
        toKeep.forEach(([key, value]) => {
          this.cache.searchResults.set(key, value);
        });
      }
    } catch (error) {
      console.error('Error cleaning search cache:', error);
    }
  }

  /**
   * Clean old filter cache entries
   */
  private static cleanFilterCache(): void {
    try {
      if (this.cache.filterResults.size > 50) {
        // Keep only the 25 most recent entries
        const entries = Array.from(this.cache.filterResults.entries());
        const toKeep = entries.slice(-25);
        
        this.cache.filterResults.clear();
        toKeep.forEach(([key, value]) => {
          this.cache.filterResults.set(key, value);
        });
      }
    } catch (error) {
      console.error('Error cleaning filter cache:', error);
    }
  }

  /**
   * Preload messages for better performance
   */
  static async preloadMessages(messageIds: string[], loadFunction: (ids: string[]) => Promise<ExtendedWebsiteMessage[]>): Promise<ExtendedWebsiteMessage[]> {
    try {
      const uncachedIds: string[] = [];
      const cachedMessages: ExtendedWebsiteMessage[] = [];

      // Check which messages are already cached
      messageIds.forEach(id => {
        const cached = this.getCachedMessage(id);
        if (cached) {
          cachedMessages.push(cached);
        } else {
          uncachedIds.push(id);
        }
      });

      // Load uncached messages
      let newMessages: ExtendedWebsiteMessage[] = [];
      if (uncachedIds.length > 0) {
        newMessages = await loadFunction(uncachedIds);
        
        // Cache the newly loaded messages
        newMessages.forEach(message => {
          this.cache.messages.set(message.id, message);
        });
      }

      // Combine and return all messages in original order
      const allMessages = [...cachedMessages, ...newMessages];
      return messageIds.map(id => allMessages.find(m => m.id === id)).filter(Boolean) as ExtendedWebsiteMessage[];
    } catch (error) {
      console.error('Error preloading messages:', error);
      throw error;
    }
  }

  /**
   * Batch update multiple messages in cache
   */
  static batchUpdateMessages(messages: ExtendedWebsiteMessage[]): void {
    try {
      messages.forEach(message => {
        this.updateCachedMessage(message);
      });
    } catch (error) {
      console.error('Error batch updating messages:', error);
    }
  }

  /**
   * Check if cache needs refresh
   */
  static needsRefresh(): boolean {
    try {
      const now = Date.now();
      return now - this.cache.lastUpdated > this.cache.ttl;
    } catch (error) {
      console.error('Error checking cache refresh:', error);
      return true;
    }
  }

  /**
   * Warm up cache with initial data
   */
  static warmUpCache(userId: string, messages: ExtendedWebsiteMessage[]): void {
    try {
      this.cacheUserMessages(userId, messages);
      
      // Pre-cache common filter combinations
      const commonFilters: MessageFilters[] = [
        { readStatus: 'unread', type: 'all', source: 'all', dateRange: {} },
        { readStatus: 'read', type: 'all', source: 'all', dateRange: {} },
        { readStatus: 'all', type: 'announcement', source: 'all', dateRange: {} }
      ];

      commonFilters.forEach(filters => {
        const filteredIds = messages
          .filter(message => this.matchesFilters(message, filters))
          .map(message => message.id);
        this.cacheFilterResults(filters, filteredIds);
      });
    } catch (error) {
      console.error('Error warming up cache:', error);
    }
  }

  /**
   * Check if message matches filters (helper for cache warming)
   */
  private static matchesFilters(message: ExtendedWebsiteMessage, filters: MessageFilters): boolean {
    try {
      // Read status filter
      if (filters.readStatus !== 'all') {
        switch (filters.readStatus) {
          case 'unread':
            if (message.viewedAt) return false;
            break;
          case 'read':
            if (!message.viewedAt) return false;
            break;
        }
      }

      // Type filter
      if (filters.type !== 'all' && message.message_type !== filters.type) {
        return false;
      }

      // Source filter
      if (filters.source !== 'all' && message.messageSource !== filters.source) {
        return false;
      }

      // Bookmarked filter
      if (filters.bookmarked && !message.isBookmarked) {
        return false;
      }

      // Date range filter
      if (filters.dateRange.start || filters.dateRange.end) {
        const messageDate = new Date(message.created_at);
        const startDate = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
        const endDate = filters.dateRange.end ? new Date(filters.dateRange.end) : null;
        
        if (startDate && messageDate < startDate) return false;
        if (endDate && messageDate > endDate) return false;
      }

      return true;
    } catch (error) {
      console.error('Error matching filters:', error);
      return false;
    }
  }
}